import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ExternalLink, Video, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const extractYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [content, setContent] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, homepageRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/homepage`)
      ]);
      setProducts(productsRes.data.products);
      setCategories(categoriesRes.data.categories);
      setContent(homepageRes.data.content);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const filteredProducts = selectedCategory
    ? products.filter(p => p.categoryId === selectedCategory)
    : products;

  const toggleDescription = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  if (!content) return null;

  return (
    <>
      {/* Hero Banner Minimale */}
      <section className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-amber-900 text-white pt-24 pb-12">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            {content.heroTitle}
          </h1>
          <p className="text-lg md:text-xl text-gray-200 mb-6 max-w-3xl mx-auto">
            {content.heroDescription}
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm md:text-base">
            {content.stats.map((stat, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <span className="font-bold">{stat.value}</span> {stat.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            <span className="bg-gradient-to-r from-gray-900 to-amber-800 bg-clip-text text-transparent">
              {content.productsTitle}
            </span>
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto mb-6">
            {content.productsSubtitle}
          </p>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="bg-gradient-to-r from-gray-50 to-amber-50/30 rounded-2xl p-5 mb-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3">{content.productsCategoryLabel}</h3>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  variant={selectedCategory === null ? "default" : "outline"}
                  onClick={() => setSelectedCategory(null)}
                  className={selectedCategory === null 
                    ? "bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-md" 
                    : "hover:border-amber-600 hover:text-amber-700"}
                >
                  {content.productsAllButtonText}
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? "default" : "outline"}
                    onClick={() => setSelectedCategory(category.id)}
                    className={selectedCategory === category.id 
                      ? "bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-md" 
                      : "hover:border-amber-600 hover:text-amber-700"}
                  >
                    {category.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product) => {
            const youtubeId = extractYouTubeId(product.youtubeUrl);
            const category = categories.find(c => c.id === product.categoryId);
            const hasVideo = !!youtubeId;
            const hasImage = !!product.image;
            
            return (
              <Card
                key={product.id}
                className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 bg-gradient-to-br from-white to-gray-50"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  {hasVideo ? (
                    <div className="relative w-full h-full bg-black">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${youtubeId}?rel=0`}
                        title={product.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="absolute inset-0"
                        loading="lazy"
                      ></iframe>
                    </div>
                  ) : hasImage ? (
                    <>
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <div className="text-gray-400 text-center">
                        <Video className="w-16 h-16 mx-auto mb-2" />
                        <p className="text-sm">Media non disponibile</p>
                      </div>
                    </div>
                  )}
                  {hasVideo && (
                    <div className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full shadow-lg">
                      <Video className="w-4 h-4" />
                    </div>
                  )}
                  {category && (
                    <div className="absolute top-2 left-2 bg-amber-600/90 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {category.name}
                    </div>
                  )}
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-amber-700 transition-colors duration-300">
                    {product.title}
                  </h3>
                  
                  {/* Description Accordion */}
                  {product.description && (
                    <div className="mb-4">
                      <button
                        onClick={() => toggleDescription(product.id)}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-amber-700 transition-colors mb-2"
                      >
                        {expandedProducts[product.id] ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Nascondi dettagli
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            Mostra dettagli prodotto
                          </>
                        )}
                      </button>
                      {expandedProducts[product.id] && (
                        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg leading-relaxed whitespace-pre-line">
                          {product.description}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <a
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-semibold group/link transition-colors duration-300"
                  >
                    {content.productsDiscoverMoreText}
                    <ExternalLink className="w-4 h-4 group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform duration-300" />
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Nessun prodotto disponibile in questa categoria.</p>
          </div>
        )}
      </div>
    </section>
    </>
  );
};